// La pestaña de un documento de Compras: `params.doc` dice cuál (oc, recepcion, factura, ingreso,
// nota, devolucion) y `params.id` cuál de ellos; sin id es uno NUEVO (con su origen: la OC que se recibe, la
// recepción que se factura o se devuelve, la factura que corrige una NC). Al crearse, la MISMA pestaña
// pasa a mostrar el documento creado (se retitula con su número).
import { ContextoProveedor } from './ContextoProveedor';
import { Devolucion } from './Devolucion';
import { EditorCompra, type TipoDocCompra } from './EditorCompra';
import { DocumentoProveedor } from './DocumentoProveedor';
import { OrdenCompra } from './OrdenCompra';
import { Recepcion } from './Recepcion';
import { RegistroRecibido } from './Recibidos';

export type DocCompra =
  'oc' | 'recepcion' | 'factura' | 'ingreso' | 'nota' | 'devolucion' | 'recibido' | 'proveedor';

export const TITULO_NUEVO: Record<DocCompra, string> = {
  oc: 'OC nueva',
  recepcion: 'Recepción',
  factura: 'Factura de compra',
  ingreso: 'Ingreso sin factura',
  nota: 'NC / ND de compra',
  devolucion: 'Devolución',
  recibido: 'Factura recibida',
  proveedor: 'Proveedor',
};

/** Clave de la pestaña: un documento existente no se abre dos veces. */
export function claveCompra(params: Record<string, string>): string {
  const doc = params['doc'] ?? 'oc';
  if (params['id']) return `compra:${doc}:${params['id']}`;
  const origen =
    params['ordenId'] ??
    params['recepcionId'] ??
    params['facturaId'] ??
    params['proveedorId'] ??
    params['corrigeId'] ??
    '';
  return `compra:${doc}:nueva:${origen}`;
}

export function rutaCompra(params: Record<string, string>): string {
  const doc = params['doc'] ?? 'oc';
  return params['id'] ? `compras/${doc}/${params['id']}` : `compras/${doc}-nueva`;
}

export function PestanaCompra({
  params,
  abrirCompra,
  titular,
  alPagar,
}: {
  params: Record<string, string>;
  abrirCompra: (params: Record<string, string>, titulo: string) => void;
  titular: (titulo: string, clave: string, params: Record<string, string>) => void;
  /** Pagar a un proveedor (abre Pago a proveedores con él puesto). */
  alPagar?: (p: { id: string; razonSocial: string }) => void;
}) {
  const doc = (params['doc'] ?? 'oc') as DocCompra;
  const id = params['id'] || undefined;
  const creado = (nuevoId: string, titulo: string, d: DocCompra = doc) => {
    const p = { doc: d, id: nuevoId };
    titular(titulo, claveCompra(p), p);
  };
  const abrir = (d: DocCompra, x: Record<string, string>, titulo: string) =>
    abrirCompra({ doc: d, ...x }, titulo);
  // Documento NUEVO (OC, guía, factura o ingreso sin factura): la ventana de digitación única, como
  // la de Ventas (2026-10-20). Al grabar, la misma pestaña pasa al documento creado (el ingreso sin
  // factura es un documento del proveedor: se ve como la factura).
  const DOC_DE: Record<TipoDocCompra, DocCompra> = {
    OCC: 'oc',
    GRC: 'recepcion',
    FCC: 'factura',
    ISF: 'factura',
  };
  // La ficha 360 del proveedor, vista desde Compras (no la comercial de Ventas).
  if (doc === 'proveedor' && id)
    return (
      <ContextoProveedor
        key={id}
        entidadId={id}
        alAbrirCompra={abrirCompra}
        alPagar={(p) => alPagar?.(p)}
      />
    );
  if (!id && (doc === 'oc' || doc === 'recepcion' || doc === 'factura' || doc === 'ingreso')) {
    const tipo: TipoDocCompra =
      doc === 'oc' ? 'OCC' : doc === 'recepcion' ? 'GRC' : doc === 'ingreso' ? 'ISF' : 'FCC';
    return (
      <EditorCompra
        key={`${doc}:${params['ordenId'] ?? ''}:${params['recepcionId'] ?? ''}:${params['corrigeId'] ?? ''}`}
        tipoInicial={tipo}
        ordenId={params['ordenId']}
        recepcionId={params['recepcionId']}
        proveedorId={params['proveedorId']}
        corrigeId={params['corrigeId']}
        alCreado={(t, nuevoId, titulo) => creado(nuevoId, titulo, DOC_DE[t])}
      />
    );
  }
  switch (doc) {
    case 'oc':
      return (
        <OrdenCompra
          key={id ?? 'nueva'}
          ordenId={id}
          alCreada={(i, n) => creado(i, n)}
          alRecibir={(ordenId, numero) => abrir('recepcion', { ordenId }, `Recibir ${numero}`)}
          alAbrirRecepcion={(i, t) => abrir('recepcion', { id: i }, t)}
        />
      );
    case 'recepcion':
      return (
        <Recepcion
          key={id ?? 'nueva'}
          recepcionId={id}
          ordenId={params['ordenId']}
          alCreada={(i, t) => creado(i, t)}
          alAbrirOc={(i, n) => abrir('oc', { id: i }, n)}
          alFacturar={(recepcionId) => abrir('factura', { recepcionId }, 'Factura de compra')}
          alDevolver={(recepcionId) => abrir('devolucion', { recepcionId }, 'Devolución')}
        />
      );
    case 'factura':
    case 'nota':
      return (
        <DocumentoProveedor
          key={id ?? 'nuevo'}
          clase={doc}
          documentoId={id}
          recepcionId={params['recepcionId']}
          facturaId={params['facturaId']}
          alRegistrado={(i, t) => creado(i, t)}
          alAbrirRecepcion={(i, t) => abrir('recepcion', { id: i }, t)}
          alNuevaNota={(facturaId) => abrir('nota', { facturaId }, 'NC / ND de compra')}
          alAbrirFactura={(i, t) => abrir('factura', { id: i }, t)}
          alCorregir={(i, t) => abrir('ingreso', { corrigeId: i }, t)}
        />
      );
    case 'proveedor':
    case 'ingreso':
      return null;
    case 'recibido':
      // Una factura recibida por XML: al registrarla, la MISMA pestaña pasa a la factura de compra.
      return (
        <RegistroRecibido
          key={id}
          recibidoId={id!}
          alRegistrado={(i, t) => creado(i, t, 'factura')}
          alAbrirFactura={(i, t) => abrir('factura', { id: i }, t)}
          alAbrirNota={(i, t) => abrir('nota', { id: i }, t)}
        />
      );
    case 'devolucion':
      return (
        <Devolucion
          key={id ?? 'nueva'}
          devolucionId={id}
          recepcionId={params['recepcionId']}
          alCreada={(i, t) => creado(i, t)}
          alAbrirRecepcion={(i, t) => abrir('recepcion', { id: i }, t)}
        />
      );
  }
}
